import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { CostCenter, useAllCostCenters } from '~/data/costCenters';
import { useEmployee } from '~/data/employee';
import { EmpBasicData, EmpPosition } from '~/data/employee/types';
import { EmployeesIndexed, useAllEmployees } from '~/data/employees';
import { useAllEnums } from '~/data/enums';
import { AllEnums } from '~/data/enums/types';
import { LegalEntity, useAllLegalEntities } from '~/data/legalEntity';
import { useAllOrgUnits, useMyUnits } from '~/data/orgunits';
import { OrgTreeData, OrganizationUnit } from '~/data/orgunits/types';
import { useOrgTree } from '~/data/orgunits/useOrgTree';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
} from '~/data/proposals';
import { UnitPosition } from '~/data/unitPositions';
import { useAllUnitPositions } from '~/data/unitPositions/useAllUnitPositions';
import {
  EmploymentProposalsDataAndConfig,
  filterAndSortEmploymentProposals,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { EditCustomField } from '~/legacy-components/CustomDataFields';
import { EmployeeProposalDecision } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHR } from '~/services/Permissions';

import {
  getManagerIds,
  mapEmpPositionProposalPutData,
  submitEmpPositionWorkflow,
} from './data';

export type CCCustomValue = {
  [key: string]: string | string[] | number | null | undefined;
};

export type ScheduleEmploymentFormUnitPositionDetails = {
  unitPosition: number;
  unitPositionPercent: number;
};

export type ScheduleEmploymentFormCostCenterDetails = {
  costCenterId: number;
  percent?: number;
  customData: { [key: string]: string | string[] | number };
  isDefault: boolean;
};

export type ScheduleEmploymentFormValues = {
  fEmpLegalEntity: string;
  fInternalPositionPosition: string;
  fEmpJobTitle: string;
  fInternalPositionInfo: string;
  fInternalPositionStartDate: Date;
  fInternalPositionUnit: number;
  fEmpCostCenter: number;
  fInternalPositionManager: string;
  fInternalPositionProposalHrId?: number;
  costCenterDetails: ScheduleEmploymentFormCostCenterDetails[];
  totalCostCenterPercentage: number;
  unitPositionDetails: ScheduleEmploymentFormUnitPositionDetails[];
  totalUnitPositionPercentage: number;
};

export type IndexedCustomFields = {
  [key: number]: EditCustomField[];
};

export type ScheduledEmploymentUnitOptions = {
  myUnits: OrganizationUnit[];
  otherUnits: OrganizationUnit[];
  allUnits: OrganizationUnit[];
  allUnitOptionsEnabled: boolean;
};

export type ScheduledEmploymentUnitSelection = {
  options: ScheduledEmploymentUnitOptions;
  onUnitChange: (newValue: number) => void;
};

export type UseScheduleEmploymentOutput = {
  legalEntities: LegalEntity[];
  unitSelection: ScheduledEmploymentUnitSelection;
  unitPositions: UnitPosition[];
  empData: EmpPosition;
  allEmployees: EmployeesIndexed;
  allEnums: AllEnums;
  empBasicData: EmpBasicData;
  managers: number[] | undefined;
  orgTree: OrgTreeData[];
  defaultData: EmpPosition;
  upcomingChangesOpen: boolean;
  proposals: EmploymentPositionProposalProps[];
  loading: boolean;
  warningOpen: boolean;
  newEngine: boolean;
  isHRUser: boolean;

  getCostCenterValues: () => CostCenter[];
  onUnitChange: (newValue: number) => void;
  onLegalEntityChange: (newValue: string) => void;
  setWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUpComingChangesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onProposalDecision: (
    proposal: EmploymentPositionProposalProps,
    decision?: EmployeeProposalDecision,
  ) => void;
};

type PropsType = {
  scheduleFormMethods: UseFormReturn<Form>;
  index: number;
  empId: number;
  resetFormAfterProposalDecision: ({
    emplDetPropData,
    empPositionPropData,
  }: {
    emplDetPropData?: EmploymentDetailsProposalProps;
    empPositionPropData?: EmploymentPositionProposalProps;
  }) => void;
  empPositionPending?: EmpPosition;
  proposalsData: EmploymentProposalsDataAndConfig;
};

export const useScheduleEmployment = (
  props: PropsType,
): UseScheduleEmploymentOutput => {
  const {
    scheduleFormMethods,
    index,
    empId,
    resetFormAfterProposalDecision,
    empPositionPending,
    proposalsData,
  } = props;

  // General Utils
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Application configs: New Engine enabled
  const { applicationConfig } = useApplicationConfig();
  const unitForwardingEnabled =
    !!applicationConfig.fComConfig?.mgrConfig.fComEnableMgrEmployeeForwarding;

  // Logged user
  const myRoles = useLoggedInUserRoles();

  // Proposals data
  const {
    positions: { proposals: unsortedProposals, mutate: mutateProposals },
    config: { proposalsEnabled },
  } = proposalsData;
  const proposals = filterAndSortEmploymentProposals(
    unsortedProposals,
  ) as unknown as EmploymentPositionProposalProps[];
  const newEngine = proposalsEnabled;

  // Data: OrgTree & Units
  const { orgTree } = useOrgTree();

  const { orgUnits: allUnits } = useAllOrgUnits();
  const { myUnits } = useMyUnits(true);

  const unitOptions: ScheduledEmploymentUnitOptions = useMemo(() => {
    const myUnitIds = myUnits.map((u) => u.fOrgUnitId);
    const otherUnits = allUnits.filter(
      (unit) => !myUnitIds.includes(unit.fOrgUnitId),
    );
    const hasOtherUnits = otherUnits.length > 0;

    return {
      allUnits,
      myUnits,
      otherUnits,
      allUnitOptionsEnabled: unitForwardingEnabled && hasOtherUnits,
    };
  }, [allUnits, myUnits, unitForwardingEnabled]);

  // Form methods & states
  const { getValues, setValue } = scheduleFormMethods;
  const [upcomingChangesOpen, setUpComingChangesOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);

  // Data: Managers
  const { data: managers, isLoading: managersIsLoading } = useSWR<number[]>(
    'managers',
    () => getManagerIds(),
    { revalidateOnFocus: false },
  );

  // Data: UnitPositions, LegalEntities, CostCenters
  const { unitPositions, isLoading: unitPositionsLoading } =
    useAllUnitPositions();
  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();
  const {
    costCenters,
    isLoading: costCentersLoading,
    hasData: costCenterHasData,
  } = useAllCostCenters();
  const { allEmployees, isLoading: employeesLoading } = useAllEmployees();
  const { employee, isLoading: employeeLoading } = useEmployee();
  const { empBasicData, empPosition } = employee;
  const { allEnums, isLoading: enumsLoading } = useAllEnums();

  const latestEmpData = useMemo(
    () => (empPositionPending ? empPositionPending : empPosition),
    [empPosition, empPositionPending],
  );

  const onProposalDecision = async (
    proposal: EmploymentPositionProposalProps,
    decision?: EmployeeProposalDecision,
  ) => {
    const submitData = mapEmpPositionProposalPutData(
      proposal,
      myRoles,
      decision,
    );
    await submitEmpPositionWorkflow(
      empId,
      submitData,
      proposal.proposalId,
    ).then((r) => {
      if (r.error) {
        enqueueSnackbar(`${t(r.error)}`, {
          variant: 'error',
        });
      } else {
        resetFormAfterProposalDecision({
          empPositionPropData:
            //INFO: if it was a reject reset back to the last approved proposal
            proposals[decision === EmployeeProposalDecision.Reject ? 1 : 0],
        });
        mutateProposals();
      }
    });
  };

  const getFormPositionUnit = useCallback(
    (empData: EmpPosition) => {
      const empDataPU = empData ? empData.fInternalPositionUnit : -1;

      // @ts-ignore
      return getValues('empPosition.fInternalPositionUnit')
        ? getValues(`schedule.${index}.empPosition.fInternalPositionUnit`)
        : empDataPU;
    },
    [getValues, index],
  );

  // Cost center utils
  const getCostCenterValues = useCallback(
    (positionUnit?: number) => {
      const targetPU = positionUnit
        ? positionUnit
        : getFormPositionUnit(latestEmpData);

      const costCentersFiltered = costCenters.filter(
        (cc) =>
          cc.hasOwnProperty('fCostCenterUnits') &&
          cc.fCostCenterUnits.indexOf(targetPU) > -1,
      );
      return costCentersFiltered.length ? costCentersFiltered : costCenters;
    },
    [latestEmpData, getFormPositionUnit, costCenters],
  );

  const findCostCenterValue = useCallback(
    (costCenterId: number, unitId?: number) =>
      getCostCenterValues(unitId).find(
        (cc) => cc.fCostCenterId === costCenterId,
      ),
    [getCostCenterValues],
  );

  // Unit selection
  const onUnitChange = (newValue: number) => {
    setValue(`schedule.${index}.empPosition.unitPositionDetails`, []);
    if (newValue === -1 || !managers) {
      setValue(`schedule.${index}.empPosition.fEmpCostCenter`, -1);
    } else {
      const orgUnit = (unitForwardingEnabled ? allUnits : myUnits).find(
        (unit) => unit.fOrgUnitId === newValue,
      );
      const selectedManager: string = orgUnit?.fOrgUnitManager.toString() ?? '';

      setValue(
        `schedule.${index}.empPosition.fInternalPositionManager`,
        selectedManager,
      );
      const empPositionValues = getValues(`schedule.${index}.empPosition`);
      if (empPositionValues) {
        const { fEmpCostCenter, costCenterDetails } = empPositionValues;

        const defaultCCLinked = findCostCenterValue(fEmpCostCenter, newValue);
        if (!defaultCCLinked) {
          setValue(`schedule.${index}.empPosition.fEmpCostCenter`, -1);

          setValue(
            `schedule.${index}.empPosition.costCenterDetails`,
            costCenterDetails.filter(
              (cc) => !!findCostCenterValue(cc.costCenterId, newValue),
            ),
          );
        }
      }
    }
  };

  const unitSelection: ScheduledEmploymentUnitSelection = {
    options: unitOptions,
    onUnitChange,
  };

  // Legal entity selection
  const onLegalEntityChange = (newValue: string) => {
    if (newValue === '') {
      setValue(`schedule.${index}.empPosition.fInternalPositionPosition`, '');
    }
  };

  return {
    legalEntities,
    loading:
      unitPositionsLoading ||
      (legalEntitiesLoading && !legalEntityHasData) ||
      (costCentersLoading && !costCenterHasData) ||
      managersIsLoading ||
      employeesLoading ||
      employeeLoading ||
      enumsLoading,
    unitSelection,
    unitPositions,
    getCostCenterValues,
    onUnitChange,
    empBasicData,
    empData: empPositionPending ? empPositionPending : empPosition,
    defaultData: empPosition,
    managers,
    allEmployees,
    allEnums,
    onLegalEntityChange,
    warningOpen,
    setWarningOpen,
    orgTree,
    upcomingChangesOpen,
    setUpComingChangesOpen,
    proposals,
    onProposalDecision,
    isHRUser: isRoleHR(myRoles),
    newEngine,
  };
};
